/* eslint-disable @typescript-eslint/no-unused-vars */
import { Reducer } from 'redux';
import { ApplicationState } from 'app/types-store';
import { ShellState, AuthState, AdminInfoData } from 'app/types-shared';
import {
  ACTION,
  AuthenticationFailed,
  DisplayModal,
  FailUserInfo,
  ReceiveLogout,
  ReceiveToken,
  ReceiveUserInfo,
  RequestUserInfo,
  UserLocaleChange,
  RefreshCache,
  ReceiveRefreshCache,
  FailRefreshCache,
  RegenerateAggregatedEnv,
  RegenerateAggregatedRisk,
  ReceiveMessagesToUserInfo,
  UpdateMessageStatus,
  FixedHeaderStatus,
  RequestLoadConsents,
  FailLoadConsents,
  ReceiveShellConfig,
  RequestVeracityPolicyCheck,
  ReceiveVeracityPolicyCheck,
  FailVeracityPolicyCheck
} from './action-types';
import { codeItemToStoreValue } from '../infrastructure/data/codes';
import { ACTION_ERROR } from 'app/infrastructure/redux';
import { reducersConfiguration } from 'dependencies/reducers';
import { ReceiveLoadConsents } from 'app/area/user-profile/my-notifications/action-types';

const reducers: { [indexer: string]: Reducer<ShellState> } = {

  [ACTION.RequestUserInfo]: (state: ShellState, action: RequestUserInfo): ShellState => {
    return {
      ...state,
      fetching: true,
      fetchingAccessInfo: true
    };
  },

  [ACTION.ReceiveUserInfo]: (state: ShellState, action: ReceiveUserInfo): ShellState => {
    const { userInfo: { language, location, unit, userGroup, ...restUserInfo } } = action as any;
    return {
      ...state,
      fetching: false,
      userInfo: {
        language: codeItemToStoreValue(language),
        location: codeItemToStoreValue(location),
        unit: codeItemToStoreValue(unit),
        userGroup: codeItemToStoreValue(userGroup),
        ...restUserInfo
      }
    };
  },

  [ACTION.ReceiveShellConfig]: (state: ShellState, action: ReceiveShellConfig): ShellState => {
    const {
      adminPanelAccess, toolsInfo, supportEmailAddress,
      version, surveyManagerAccess, helpUrl, manageSurveysUrl, feedbackUrl
    } = action;

    return {
      ...state,
      fetchingAccessInfo: false,
      userInfo: {
        ...state.userInfo,
        access: { adminPanel: adminPanelAccess as AdminInfoData, tools: toolsInfo, surveyManager: surveyManagerAccess }
      },
      version: version,
      supportEmailAddress: supportEmailAddress.emailAddress,
      helpUrl,
      manageSurveysUrl,
      feedbackUrl
    };
  },

  [ACTION.FailUserInfo]: (state: ShellState, action: FailUserInfo): ShellState => {
    return {
      ...state,
      authState: AuthState.AuthenticationFailed
    };
  },

  [ACTION.ReceiveMessagesToUserInfo]: (state: ShellState, action: ReceiveMessagesToUserInfo): ShellState => {

    return {
      ...state,
      messagesToUser: action.messagesToUserInfo
    };
  },

  [ACTION.UpdateMessageStatus]: (state: ShellState, action: UpdateMessageStatus): ShellState => {
    return {
      ...state,
      updatingMessagesToUser: action.updating,
      messagesToUser: state.messagesToUser.map(item => item.id === action.id ? { ...item, updating: action.updating } : item)
    };
  },

  [ACTION.ReceiveToken]: (state: ShellState, action: ReceiveToken): ShellState => {

    return {
      ...state,
      authState: AuthState.Authenticated
    };
  },

  [ACTION.ReceiveLogout]: (state: ShellState, action: ReceiveLogout): ShellState => {

    return {
      ...state,
      authState: AuthState.LoggedOut
    };
  },

  [ACTION.AuthenticationFailed]: (state: ShellState, action: AuthenticationFailed): ShellState => {

    return {
      ...state,
      authState: AuthState.AuthenticationFailed
    };
  },

  [ACTION.DisplayModal]: (state: ShellState, action: DisplayModal): ShellState => {
    return {
      ...state,
      selectedModal: action.name
    };
  },

  [ACTION.ChangeUserLocale]: (state: ShellState, action: UserLocaleChange): ShellState => {
    return {
      ...state,
      locale: action.locale
    };
  },

  [ACTION.RefreshCache]: (state: ShellState, action: RefreshCache): ShellState => {
    return {
      ...state,
      fetchingRefreshCache: true
    };
  },
  [ACTION.ReceiveRefreshCache]: (state: ShellState, action: ReceiveRefreshCache): ShellState => {
    return {
      ...state,
      fetchingRefreshCache: false
    };
  },
  [ACTION.FailRefreshCache]: (state: ShellState, action: FailRefreshCache): ShellState => {
    return {
      ...state,
      fetchingRefreshCache: false
    };
  },

  [ACTION.RegenerateAggregatedEnv]: (state: ShellState, action: RegenerateAggregatedEnv): ShellState => {
    return {
      ...state,
      fetchingRegenerateAggregated: action.fetching
    };
  },

  [ACTION.RegenerateAggregatedRisk]: (state: ShellState, action: RegenerateAggregatedRisk): ShellState => {
    return {
      ...state,
      fetchingRegenerateAggregated: action.fetching
    };
  },

  [ACTION.FixedHeaderStatus]: (state: ShellState, action: FixedHeaderStatus): ShellState => {
    return {
      ...state,
      fixedHeader: action.fixed
    };
  },

  [ACTION.REQUEST_LOAD_CONSENTS]: (state: ShellState, action: RequestLoadConsents): ShellState => {
    return {
      ...state,
      fetchingLoadConsents: true,
      consents: undefined
    };
  },

  [ACTION.RECEIVE_LOAD_CONSENTS]: (state: ShellState, action: ReceiveLoadConsents): ShellState => {
    return {
      ...state,
      consents: action.consents,
      fetchingLoadConsents: false
    };
  },

  [ACTION.FAIL_LOAD_CONSENTS]: (state: ShellState, action: FailLoadConsents): ShellState => {
    return {
      ...state,
      fetchingLoadConsents: false
    };
  },

  [ACTION.REQUEST_VERACITY_POLICY_CHECK]: (state: ShellState, action: RequestVeracityPolicyCheck): ShellState => {
    return {
      ...state,
      fetchingVeracityPolicyCheck: true
    };
  },

  [ACTION.RECEIVE_VERACITY_POLICY_CHECK]: (state: ShellState, action: ReceiveVeracityPolicyCheck): ShellState => {
    return {
      ...state,
      fetchingVeracityPolicyCheck: false,
      isVeracityPolicyAccepted: action.isPolicyAccepted
    };
  },

  [ACTION.FAIL_VERACITY_POLICY_CHECK]: (state: ShellState, action: FailVeracityPolicyCheck): ShellState => {
    return {
      ...state,
      fetchingVeracityPolicyCheck: false,
      isVeracityPolicyAccepted: false
    };
  }
};
export const reducer: Reducer<ShellState> = (state: ShellState = reducersConfiguration.INITIAL_APP_STATE.app, action: any) => {
  const r = reducers[action.type];
  return (r && r(state, action)) || state;
};

// Manage appState when actionCreators throws an error
export const errorsReducer: Reducer<ApplicationState> = (state: ApplicationState, action: any) => {

  if (action.type !== ACTION_ERROR) {
    return state;
  }

  return {

    ...state,

    ...{

      app: {
        ...state.app,

        errors: [
          ...state.app.errors,
          ...[
            { error: action.error, message: action.message }
          ]
        ]

      }

    }
  };
};
